/**
 * HEIC Converter Utility
 *
 * This module provides functions to detect and convert HEIC images to JPEG format.
 * Uses libheif-js directly for maximum compatibility with modern iOS HEIC formats.
 */

// @ts-ignore - using CommonJS import
import libheif from "libheif-js/wasm-bundle";

/**
 * Checks if a file is a HEIC image
 */
export const isHeicFile = (file: File): boolean => {
	const fileName = file.name.toLowerCase();
	const fileType = file.type.toLowerCase();

	return (
		fileName.endsWith(".heic") ||
		fileName.endsWith(".heif") ||
		fileType === "image/heic" ||
		fileType === "image/heif"
	);
};

/**
 * Converts a HEIC file to JPEG format using libheif-js
 *
 * @param file - The HEIC file to convert
 * @returns Promise<Blob> - The converted JPEG blob
 */
export const convertHeicToJpeg = async (file: File): Promise<Blob> => {
	try {
		console.log("[HEIC Converter] Converting HEIC file to JPEG:", file.name);

		// Read the file as an ArrayBuffer
		const arrayBuffer = await file.arrayBuffer();
		const data = new Uint8Array(arrayBuffer);

		console.log("[HEIC Converter] File loaded, size:", file.size, "bytes");

		// Create decoder instance
		const decoder = new libheif.HeifDecoder();

		// Decode the HEIC file
		const images = decoder.decode(data);

		if (!images || images.length === 0) {
			throw new Error("No images found in HEIC file");
		}

		// Get the first image
		const image = images[0];
		const width = image.get_width();
		const height = image.get_height();

		console.log("[HEIC Converter] Decoded image:", { width, height });

		// Create a canvas to draw the decoded image
		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext("2d");

		if (!ctx) {
			throw new Error("Failed to get canvas context");
		}

		// Get image data in RGBA format using display callback
		const imageData = await new Promise<ImageData>((resolve, reject) => {
			image.display(
				{ data: new Uint8ClampedArray(width * height * 4), width, height },
				(displayData: any) => {
					if (!displayData) {
						return reject(new Error("Failed to display image data"));
					}

					// Create ImageData from the display data
					const imgData = new ImageData(displayData.data, width, height);
					resolve(imgData);
				},
			);
		});

		// Draw the image data onto the canvas
		ctx.putImageData(imageData, 0, 0);

		// Convert canvas to JPEG blob
		return new Promise<Blob>((resolve, reject) => {
			canvas.toBlob(
				(blob) => {
					if (blob) {
						console.log(
							"[HEIC Converter] Conversion successful. Original size:",
							file.size,
							"Converted size:",
							blob.size,
						);
						resolve(blob);
					} else {
						reject(new Error("Failed to create blob from canvas"));
					}
				},
				"image/jpeg",
				0.92, // High quality JPEG
			);
		});
	} catch (error) {
		console.error("[HEIC Converter] Error converting HEIC file:", error);

		// Provide more helpful error message
		const errorMessage =
			error instanceof Error ? error.message : "Unknown error";
		throw new Error(
			`Failed to convert HEIC image: ${errorMessage}. Please ensure the file is a valid HEIC image.`,
		);
	}
};

/**
 * Processes an image file, converting it to JPEG if it's HEIC, otherwise returning the original file
 *
 * @param file - The file to process
 * @returns Promise<Blob> - The processed file as a Blob
 */
export const processImageFile = async (file: File): Promise<Blob> => {
	if (isHeicFile(file)) {
		return await convertHeicToJpeg(file);
	}
	return file;
};
